/**
 * Root route — sets up the page chrome (header, sidebar, mobile toolbar)
 * and gates everything behind authentication. Unauthenticated users land on
 * the login screen instead.
 */

import { useEffect, useState } from "react";
import {
  Outlet,
  createRootRouteWithContext,
  useNavigate,
  useRouterState,
} from "@tanstack/react-router";
import type { QueryClient } from "@tanstack/react-query";
import { Header } from "../components/Header";
import { Sidebar } from "../components/Sidebar";
import { MobileToolbar } from "../components/MobileToolbar";
import { LoginScreen } from "../components/LoginScreen";
import { NewNoteFab } from "../components/NewNoteFab";
import { ShortcutsHelp } from "../components/ShortcutsHelp";
import { useMe } from "../api/hooks";
import { ApiError } from "../lib/api";
import { useGlobalShortcuts } from "../lib/shortcuts";
import { useLiveSync } from "../lib/useLiveSync";

interface RouterContext {
  queryClient: QueryClient;
}

export const Route = createRootRouteWithContext<RouterContext>()({
  component: RootComponent,
});

function RootComponent() {
  const path = useRouterState({ select: (s) => s.location.pathname });
  const isPublicShare = path.startsWith("/s/");
  const meQ = useMe({ enabled: !isPublicShare });
  const navigate = useNavigate();
  const [drawer, setDrawer] = useState(false);
  const [search, setSearch] = useState("");

  // Register global keyboard shortcuts (c → new note, / or ⌘K → search,
  // ? → help). The hook itself bails out inside editable fields.
  useGlobalShortcuts();

  // Live-sync: refetch when notes change elsewhere (other devices, tabs, MCP).
  useLiveSync(!!meQ.data);

  // When the user types in the global search box, route them to /search.
  useEffect(() => {
    if (search && path !== "/search") {
      navigate({ to: "/search", search: { q: search } });
    }
  }, [search, path, navigate]);

  // After signing in, resume an OAuth consent flow we were bounced out of
  // (worker route, so use a full navigation). Restricted to /oauth/authorize to
  // prevent open redirects.
  useEffect(() => {
    if (!meQ.data) return;
    const rt = new URLSearchParams(window.location.search).get("returnTo");
    if (rt && rt.startsWith("/oauth/authorize")) window.location.replace(rt);
  }, [meQ.data]);

  if (isPublicShare) {
    // Public shares get their own (chrome-less) layout via the route component.
    return <Outlet />;
  }

  if (meQ.isPending) {
    return (
      <div
        className="flex items-center justify-center h-full text-sm"
        style={{ color: "var(--fg-muted)" }}
      >
        Loading…
      </div>
    );
  }
  if (meQ.error) {
    const err = meQ.error as Error;
    if (err instanceof ApiError && err.status === 401) {
      return <LoginScreen />;
    }
    return <LoginScreen error={err.message} />;
  }

  return (
    <div className="app-shell">
      <Header onOpenDrawer={() => setDrawer(true)} search={search} onSearch={setSearch} />
      <div className="flex-1 flex overflow-hidden min-h-0">
        <aside
          className="hidden lg:flex w-60 border-r overflow-y-auto"
          style={{ borderColor: "var(--border)", background: "var(--bg-elevated)" }}
        >
          <Sidebar />
        </aside>
        {/* The toolbar is an in-flow sibling below, so content needs no bottom
         * clearance — main simply fills the space between header and toolbar. */}
        <main className="flex-1 overflow-y-auto pb-4 lg:pb-6">
          <Outlet />
        </main>
      </div>
      <MobileToolbar />
      <NewNoteFab />

      {drawer && (
        <>
          <div className="drawer-backdrop lg:hidden" onClick={() => setDrawer(false)} />
          <aside
            className="drawer-panel lg:hidden border-r"
            style={{ borderColor: "var(--border)" }}
          >
            <Sidebar onNavigate={() => setDrawer(false)} />
          </aside>
        </>
      )}

      <ShortcutsHelp />
    </div>
  );
}
